/*
This component uses the all dialogues endpoints to reduce the number of api calls
*/
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  useDisclosure,
  Button,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import "./World.css";
import ContainerBackground from "../ContainerBackground/ContainerBackground";
import { useTheme } from "../ThemeContext/ThemeContext";
import PanicButton from "../PanicButton/PanicButton";
import MessengerSlimette from "../MessengerSlimette/MessengerSlimette";
import ModalContentBody from "../WorldModal/WorldModal";
import ResourcesModal from "./ResourcesModal/ResourcesModal";
import EndScreen from "../EndScreen/EndScreen";
import FinnNeutral from "../../Assets/Images/Characters/Finn/Finn_neutral_light.png";
import FinnHappy from "../../Assets/Images/Characters/Finn/Finn_happy_light.png";
import useFetchDialogue from "../../hooks/useFetchDialogue";
import AccessibilityMenu from "../World/AccessibilityMenu";
import BackToMapButton from "../World/BackToMapButton";
import DialogueQuestionOverlay from "../World/DialogueQuestionOverlay";
import {
  parseYesResources,
  extractResources,
  parseSpecialResources,
} from "../../utils/dialogueUtils";
import { useWorldData } from "../../context/WorldContext";
import { useAuth } from "../auth/AuthContext";
import {
  getUserProfile,
  getUserProgress,
  setUserProfile,
  setUserProgress,
} from "../../utils/userUtils";
import { getActivity } from "../../utils/activityUtils";
import resourceList from "../../Assets/resourceList.json";

const World = () => {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const location = useLocation();
  const { progressDialogue } = location.state || {};
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setIsLoggedIn(!!user);
  }, [user]);

  // Use global world data state
  const { currentWorldOrder } = useWorldData();

  const {
    currentDialogue,
    activityName,
    fullDialogueText,
    inputType,
    dialogue,
    selectionOptions,
    specialInstruction,
    isDialogueComplete,
    nextDefaultId,
    staticWorldId,
    intervalId,
    isLoading,
    hasPreviousDialogue,
    setIsLoading,
    fetchDialogue,
    setDialogue,
    fetchAllDialogues,
    fetchFirstDialogue,
    setIsDialogueComplete,
  } = useFetchDialogue(currentWorldOrder);

  // State variables
  const [finnImage, setFinnImage] = useState(FinnNeutral);
  const MotionModalContent = motion(ModalContent);
  const [showEndModal, setShowEndModal] = useState(false);
  const [arrowPointer, setArrowPointer] = useState(false);
  const [graphicSrcs, setGraphicSrcs] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isLoginOpen,
    onOpen: onLoginOpen,
    onClose: onLoginClose,
  } = useDisclosure();
  const {
    isOpen: isSupportOpen,
    onOpen: onSupportOpen,
    onClose: onSupportClose,
  } = useDisclosure();
  const {
    isOpen: isResourcesOpen,
    onOpen: onResourcesOpen,
    onClose: onResourcesClose,
  } = useDisclosure();
  const cancelLoginRef = useRef();
  const cancelSupportRef = useRef();
  const [slimetteVisible, setSlimetteVisible] = useState(false);
  const localFontSize = parseInt(localStorage.getItem("fontSize") ?? "18");
  const [fontSize, setFontSize] = useState(localFontSize);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    localStorage.setItem("fontSize", fontSize.toString());
  }, [fontSize]);

  // Fetch all dialogues when the component mounts
  useEffect(() => {
    fetchAllDialogues(currentWorldOrder);
  }, [fetchAllDialogues]);

  // Open modal when dialogue is complete and input is required
  useEffect(() => {
    if (isDialogueComplete && inputType !== "none") {
      onOpen();
    }
  }, [isDialogueComplete, inputType, onOpen]);

  // Fetch the initial dialogue based on progress or world order
  useEffect(() => {
    if (progressDialogue) {
      fetchDialogue(progressDialogue);
    } else {
      fetchFirstDialogue();
    }
  }, [fetchDialogue, progressDialogue, currentWorldOrder]);

  // Handle special instructions when they are set
  useEffect(() => {
    if (specialInstruction === "@highlight{panicButton}") {
      setArrowPointer(true);
    } else if (
      specialInstruction === "@highlight{supportTab}" &&
      isDialogueComplete
    ) {
      onSupportOpen();
    } else if (specialInstruction.startsWith("@graphic{/")) {
      const imagePaths = specialInstruction.slice(10, -1).split(", ");
      const imageUrls = imagePaths.map((path) => `./${path.trim()}`);
      Promise.all(
        imageUrls.map((url) =>
          fetch(url)
            .then((response) => response.blob())
            .then((blob) => URL.createObjectURL(blob))
        )
      )
        .then((urls) => setGraphicSrcs(urls))
        .catch((error) => console.error("Error fetching images:", error));
    }
  }, [specialInstruction, isDialogueComplete, onLoginOpen, onSupportOpen]);

  // Handle user input submission
  const handleUserInputSubmit = async (inputData) => {
    const yesOption =
      selectionOptions.find(
        (option) => option.label === "Yes" && option.id === inputData.radio
      ) || "";
    if (yesOption) {
      setFinnImage(FinnHappy); // Change Finn's mood to happy
    } else {
      setFinnImage(FinnNeutral);
    }

    // Handle resource fetching based on special instructions
    if (yesOption && specialInstruction.startsWith("@resources{@Yes{")) {
      const resourceIds = parseYesResources(specialInstruction);
      fetchResources(resourceIds);
    }

    if (
      specialInstruction.startsWith("@resources{@") &&
      !specialInstruction.startsWith("@resources{@Yes{")
    ) {
      const selectedResources = inputData.checkboxes
        .map((id) => {
          const option = selectionOptions.find((option) => option.id === id);
          return option ? option.label : null; // Return the option name if found, null otherwise
        })
        .filter((name) => name !== null); // Filter out any nulls (in case an ID wasn't found)

      if (selectedResources.length > 0) {
        const resourceMap = extractResources(specialInstruction);
        const resourceIds = parseSpecialResources(
          resourceMap,
          selectedResources
        );

        fetchResources(resourceIds);
      }
    }

    const profileRequiresUpdate = currentDialogue.updateUserProfile;
    const userProfileField = currentDialogue.userProfileField;

    // Update user profile based on dialogue requirements
    const selectedPronouns = inputData.checkboxes
      .map((id) => {
        const option = selectionOptions.find((option) => option.id === id);
        return option ? option.label : null;
      })
      .filter((name) => name !== null);

    const selectedGender = inputData.checkboxes
      .map((id) => {
        const option = selectionOptions.find((option) => option.id === id);
        return option ? option.label : null;
      })
      .filter((label) => label !== null);

    // Prepare the data for updating the user profile based on the field to be updated
    let updateData = getUserProfile();
    if (userProfileField === "name") {
      updateData.name = inputData.text;
      // Storing inputData.text in localStorage as userName
      localStorage.setItem("userName", inputData.text);
    } else if (userProfileField === "gender") {
      // Join the selected pronouns into a single string
      updateData.gender = selectedGender.join(", ");
    } else if (userProfileField === "pronoun") {
      // Join the selected pronouns into a single string
      updateData.pronoun = selectedPronouns.join(", ");
    }

    if (profileRequiresUpdate && Object.keys(updateData).length > 0) {
      setUserProfile(updateData);
    }

    if (inputData.nextDialogue) {
      saveProgress(inputData.nextDialogue);
      fetchDialogue(inputData.nextDialogue);
    }
    onClose();
    setSlimetteVisible(true);
  };

  // Fetch activity data
  const fetchActivity = async (activityName) => {
    setIsLoading(true);
    const activityData = getActivity(activityName);
    navigate(`/activity/${activityName}`, {
      state: { activityData, from: "World" },
    });
    setIsLoading(false);
  };

  // Handle the "Next" button click
  const handleNextClick = () => {
    if (!isDialogueComplete) {
      clearInterval(intervalId.current);
      setDialogue(fullDialogueText);
      setIsDialogueComplete(true);
    } else if (activityName !== "") {
      saveProgress(nextDefaultId);
      fetchActivity(activityName);
      setArrowPointer(false);
      setGraphicSrcs([]);
    } else if (nextDefaultId) {
      saveProgress(nextDefaultId);
      fetchDialogue(nextDefaultId);
      setArrowPointer(false);
      setGraphicSrcs([]);
    } else if (!nextDefaultId) {
      saveProgress(null);
      setShowEndModal(true);
      setArrowPointer(false);
      setGraphicSrcs([]);
    }

    setSlimetteVisible(false);
    setFinnImage(FinnNeutral);
  };

  const handlePreviousClick = async () => {
    if (hasPreviousDialogue) {
      const previousDialogueId = await undoProgress();
      fetchDialogue(previousDialogueId);
      setArrowPointer(false);
      setGraphicSrcs([]);
    }
  };

  const undoProgress = async () => {
    const progress = getUserProgress();
    const worldProgress = progress.worldProgresses.find(
      (world) => world.world === staticWorldId
    );
    const previousDiaogueId = worldProgress.dialogueHistory.pop();
    if (!previousDiaogueId) {
      console.error("No previous dialogue found");
      return currentDialogue._id;
    }
    worldProgress.nextDialogue = previousDiaogueId;
    worldProgress.status = "inProgress";

    progress.lastWorld = staticWorldId;
    progress.lastDialogue = previousDiaogueId;
    progress.status = "inProgress";
    setUserProgress(progress);
    return previousDiaogueId;
  };
  // Save user progress
  const saveProgress = async (dialogueId) => {
    const progress = getUserProgress();
    const worldProgress = progress.worldProgresses.find(
      (world) => world.world === staticWorldId
    );
    if (!worldProgress.dialogueHistory.includes(currentDialogue._id)) {
      worldProgress.dialogueHistory = [
        ...worldProgress.dialogueHistory,
        currentDialogue._id,
      ];
    }
    worldProgress.nextDialogue = dialogueId;
    worldProgress.status = dialogueId ? "inProgress" : "completed";
    progress.lastWorld = staticWorldId;
    progress.lastDialogue = dialogueId;
    progress.status = dialogueId ? "inProgress" : "completed";
    setUserProgress(progress);
  };

  // Fetch resources
  const fetchResources = (resourceIds) => {
    const data = resourceList;
    const filteredResources = data.filter((resource) =>
      resourceIds.includes(resource.id)
    );
    setResources(filteredResources);
    onResourcesOpen();
  };

  return (
    <ContainerBackground theme={currentTheme}>
      <div className="world2-overlay">
        <div className="world2-container">
          <BackToMapButton />
          <AccessibilityMenu fontSize={fontSize} setFontSize={setFontSize} />
          <MessengerSlimette
            isVisible={slimetteVisible}
            message="Thank you for answering! Click 'Next' button to proceed"
            slimetteType={"validator"}
          />
          <img
            src={finnImage}
            alt="Finn"
            className="finn-character finn-slide-in"
          />
          <div
            className="finn-speech-bubble"
            style={{ fontSize: `${fontSize}px` }}
          >
            {isLoading ? (
              <Center>
                <Spinner />
              </Center>
            ) : (
              <p
                dangerouslySetInnerHTML={{ __html: dialogue }}
                style={{ paddingBottom: "30px", whiteSpace: "pre-line" }}
              />
            )}
            <div className="previous-button-container">
              {!isLoading && hasPreviousDialogue && (
                <Button onClick={handlePreviousClick}>Previous</Button>
              )}
            </div>
            <div className="next-button-container">
              {!isLoading && <Button onClick={handleNextClick}>Next</Button>}
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => {}}
        isClosable={false}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        motionPreset="none"
      >
        <ModalOverlay bg={"none"} />
        <MotionModalContent
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0, 1] }}
          transition={{ duration: 2, times: [0, 0.9, 1] }}
        >
          <ModalHeader>Enter or Select Response</ModalHeader>
          <ModalBody>
            <ModalContentBody
              inputType={inputType}
              selectionOptions={selectionOptions}
              onInputSubmit={handleUserInputSubmit}
            />
          </ModalBody>
          <ModalFooter />
        </MotionModalContent>
      </Modal>
      <DialogueQuestionOverlay
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        leastDestructiveRef={cancelLoginRef}
        title="Login Required"
        description="Do you want to log in now?"
        redirectPath="/loginfs"
      />
      <DialogueQuestionOverlay
        isOpen={isSupportOpen}
        onClose={onSupportClose}
        leastDestructiveRef={cancelSupportRef}
        title="Resources for you"
        description="Do you want to access the Support resource now?"
        redirectPath="/support"
        shouldOpenInNewTab={true}
      />
      <div className="graphic-container">
        {graphicSrcs.map((src, index) => (
          <img
            key={index}
            src={src}
            alt={`Special Graphic ${index + 1}`}
            className="graphic"
          />
        ))}
      </div>
      <ResourcesModal
        isOpen={isResourcesOpen}
        onClose={onResourcesClose}
        resources={resources}
      />
      <EndScreen isVisible={showEndModal} />
      <PanicButton isVisible={arrowPointer} />
    </ContainerBackground>
  );
};

export default World;
